/**
 * Archivo: ficheros-empleados.service.ts
 * Descripción: Este archivo contiene la definición del servicio FicherosEmpleadosService, el cual se utiliza para realizar operaciones relacionadas con los ficheros de empleados.
 * Fecha de creación: 7/01/2024
 * Autor: José Rodríguez
 * Comentarios: Utiliza el servicio web API REST empleados.
 */
import {
  EmployeesFileRejectDTO,
  EmployeesFiles,
} from './../../models/personal/ficheros.model';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseService } from '../base';
import { MatSnackBar } from '@angular/material/snack-bar'; 
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../../models/base';
import {
  EmployeesFileUploadDTO,
  EmployeesFilesDTO,
  Files,
} from '../../models/personal/ficheros.model';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';

const EMPLEADOS_ENDPOINT = '/employees/';
const FILES_ENDPOINT = '/files/';

@Injectable({ providedIn: 'root' })
export class FicherosEmpleadosService extends BaseService {
  // #region Propiedades del servicio

  private importeValidadoPorEmpleado: number = 0;
  ivpe$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  importeValidadoPorEmpleado$: Observable<number> = this.ivpe$.asObservable();

  listaFicherosEmpleadosDTO: EmployeesFilesDTO[] = null;
  lfe$: BehaviorSubject<EmployeesFilesDTO[]> = new BehaviorSubject<EmployeesFilesDTO[]>([]);
  listaFicherosEmpleadosDTO$: Observable<EmployeesFilesDTO[]> = this.lfe$.asObservable();

  listaFicherosPorEmpleado: EmployeesFilesDTO[] = null;
  lfpe$: BehaviorSubject<EmployeesFilesDTO[]> = new BehaviorSubject<EmployeesFilesDTO[]>([]);
  listaFicherosPorEmpleado$: Observable<EmployeesFilesDTO[]> = this.lfpe$.asObservable();

  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _uploadingFile$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  uploadingFile$: Observable<boolean> = this._uploadingFile$.asObservable();
  private _downloadingFile$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  downloadingFile$: Observable<boolean> = this._downloadingFile$.asObservable();

  tiposGastos: string[] = [
    'Tarjeta empresa',
    'Efectivo empresa',
    'Fondos propios',
  ];
  // #endregion

  constructor(
    private httpClient: HttpClient,
    protected snackBar: MatSnackBar
  ) {
    super(snackBar);
  }

  // #region Métodos de consulta a la API

  /**
   * Elimina un archivo de empleado.
   * @param idEmployee - ID del empleado.
   * @param idEmployeeFile - ID del archivo de empleado.
   * @returns Un Observable con true/false.
   */
  deleteFicheroEmpleado(
    idEmployee: number,
    idEmployeeFile: number
  ): Observable<boolean> {
    this.loading$.next(true);

    const url = `${environment.apiUrl}${EMPLEADOS_ENDPOINT}deletefile/${idEmployee}/${idEmployeeFile}`;

    return this.httpClient.delete<ApiResponse<boolean>>(url).pipe(
      map((resultado) => {
        if (resultado && resultado.data) {
          this.snackBar.open('Fichero eliminado correctamente', 'Cerrar', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          });

          this.getListFicherosEmpleadosDTO();

          if (this.listaFicherosPorEmpleado && this.listaFicherosPorEmpleado.length > 0) {
            this.listaFicherosPorEmpleado = this.listaFicherosPorEmpleado.filter(
              (fichero) => fichero.relacion.id !== idEmployeeFile
            );
            this.lfpe$.next(this.listaFicherosPorEmpleado);
          }

          return true;
        }

        if (resultado && resultado.error) {
          this.procesarError(resultado);
        }

        return false;
      }),
      catchError((error) => {
        this.procesarError(error);
        return of(false);
      }),
      finalize(() => {
        this.loading$.next(false);
      })
    );
  }

  /**
   * Obtiene la lista de ficheros de empleados por usuario.
   */
  getListFicherosEmpleadosDTO(soloNoRechazados: boolean = false, mostrarAvisoSiVacio: boolean = true): void {
    this.loading$.next(true);

    this.httpClient
      .get<ApiResponse<EmployeesFilesDTO[]>>(
        environment.apiUrl +
          EMPLEADOS_ENDPOINT +
          'filesbyuser/' +
          (soloNoRechazados ? 'true' : '')
      )
      .pipe(
        finalize(() => {
          this.loading$.next(false);
        })
      )
      .subscribe({
        next: (resultado) => {
          if (resultado && resultado.data) {
            this.listaFicherosEmpleadosDTO = resultado.data;
            this.lfe$.next(this.listaFicherosEmpleadosDTO);

            if (this.listaFicherosEmpleadosDTO.length == 0 && mostrarAvisoSiVacio) {
              this.snackBar.open(
                'No hay ficheros para el usuario actual o todos han sido rechazados.',
                'Cerrar',
                {
                  duration: 5000,
                  horizontalPosition: 'center',
                  verticalPosition: 'bottom',
                }
              );
            }
          } else if (resultado && resultado.error) {
            this.procesarError(resultado);
          }
        },
        error: (error) => {
          this.procesarError(error);
        },
      });
  }

  /**
   * Obtiene la lista de ficheros de empleados DTO por ID de empleado.
   * @param employeeId El ID del empleado.
   * @param showEmptyNotice Muestra el aviso "No hay ficheros..." cuando la lista viene vacía.
   * Por defecto true (vista dedicada a ficheros); ponlo a false en cargas de fondo/silenciosas
   * (p.ej. al abrir la ficha de un empleado) donde no tener ficheros es el caso normal.
   */
  getListFicherosEmpleadosDTOByEmployee(employeeId: number, showEmptyNotice: boolean = true): void {
    this.loading$.next(true);
    this.resetearImporteValidadoPorEmpleado();

    this.httpClient
      .get<ApiResponse<EmployeesFilesDTO[]>>(
        environment.apiUrl + EMPLEADOS_ENDPOINT + 'files/' + employeeId
      )
      .pipe(
        finalize(() => {
          this.loading$.next(false);
        })
      )
      .subscribe({
        next: (resultado) => {
          if (resultado && resultado.data) {
            this.listaFicherosPorEmpleado = resultado.data;
            this.lfpe$.next(this.listaFicherosPorEmpleado);

            this.listaFicherosPorEmpleado.forEach((fichero) => {
              if (fichero.relacion.aprobado) {
                this.importeValidadoPorEmpleado += fichero.relacion.totalImporte;
              }
            });
            this.ivpe$.next(this.importeValidadoPorEmpleado);

            if (this.listaFicherosPorEmpleado.length == 0 && showEmptyNotice) {
              this.snackBar.open(
                'No hay ficheros para el empleado seleccionado',
                'Cerrar',
                {
                  duration: 5000,
                  horizontalPosition: 'center',
                  verticalPosition: 'bottom',
                }
              );
            }
          } else if (resultado && resultado.error) {
            this.procesarError(resultado);
          }
        },
        error: (error) => {
          this.procesarError(error);
        },
      });
  }

  /**
   * Obtiene un fichero de empleado por su identificador de archivo.
   * Devuelve null si no existe o no se puede obtener.
   * @param fileUid El identificador único del archivo.
   */
  getFicheroEmpleadoPorFileUid(fileUid: string): Observable<EmployeesFiles | null> {
    this.loading$.next(true);

    return this.httpClient
      .get<ApiResponse<EmployeesFiles>>(
        environment.apiUrl + EMPLEADOS_ENDPOINT + 'filebyuid/' + fileUid
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          if (resultado && resultado.error) {
            this.procesarError(resultado);
          }

          return null;
        }),
        catchError(() => {
          return of(null);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Rechaza un fichero de empleado.
   * @param idEmployeeFile - El ID del fichero de empleado a rechazar.
   * @param motivoRechazo - El motivo del rechazo.
   */
  rechazarFicheroEmpleado(
    idEmployeeFile: number,
    motivoRechazo: string
  ): Observable<boolean> {
    this.loading$.next(true);

    const datosRechazo: EmployeesFileRejectDTO = {
      id: idEmployeeFile,
      motivo: motivoRechazo,
    };

    const url = `${environment.apiUrl}${EMPLEADOS_ENDPOINT}file/reject`;

    return this.httpClient.post<ApiResponse<boolean>>(url, datosRechazo).pipe(
      map((resultado) => {
        if (resultado && resultado.data) {
          this.snackBar.open('Fichero rechazado correctamente', 'Cerrar', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          });

          this.listaFicherosEmpleadosDTO = (this.listaFicherosEmpleadosDTO || []).filter(
            (fichero) => fichero.relacion.id !== idEmployeeFile
          );
          this.lfe$.next(this.listaFicherosEmpleadosDTO);

          return true;
        }

        if (resultado && resultado.error) {
          this.procesarError(resultado);
        }

        return false;
      }),
      catchError((error) => {
        this.procesarError(error);
        return of(false);
      }),
      finalize(() => {
        this.loading$.next(false);
      })
    );
  }

  /**
   * Resetea el importe validado por empleado.
   */
  resetearImporteValidadoPorEmpleado(): void {
    this.importeValidadoPorEmpleado = 0;
    this.ivpe$.next(this.importeValidadoPorEmpleado);
  }

  /**
   * Envía un documento al servidor para ser guardado en relación a un empleado.
   */
  saveDocument(employeeId: number, file: File, tipoGasto: string): void {
    const url = `${environment.apiUrl}${EMPLEADOS_ENDPOINT}newfile/${file.name}/${tipoGasto}/${employeeId}`;
    this.loading$.next(true);

    const formData = new FormData();
    formData.append('file', file);

    this.httpClient
      .post<ApiResponse<Files>>(url, formData)
      .pipe(
        finalize(() => {
          this.loading$.next(false);
        })
      )
      .subscribe({
        next: (resultado) => {
          if (resultado && resultado.data) {
            this.snackBar.open('Fichero guardado correctamente', 'Cerrar', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
            });

            this.getListFicherosEmpleadosDTO();
          } else if (resultado && resultado.error) {
            this.procesarError(resultado);
          }
        },
        error: (error) => {
          this.procesarError(error);
        },
      });
  }

  /**
   * Guarda una imagen asociada a un empleado.
   */
  saveImage(
    employeeId: number,
    imageBase64: string,
    tipoGasto: string,
    type: string,
    size: number,
    nombreArchivo: string
  ): void {
    this.loading$.next(true);

    const image = this.inicializarImage(
      employeeId,
      imageBase64,
      type,
      size,
      tipoGasto,
      nombreArchivo
    );

    this.httpClient
      .post<ApiResponse<Files>>(
        environment.apiUrl + EMPLEADOS_ENDPOINT + 'newfilebase64',
        image
      )
      .pipe(
        finalize(() => {
          this.loading$.next(false);
        })
      )
      .subscribe({
        next: (resultado) => {
          if (resultado && resultado.data) {
            this.snackBar.open('Fichero guardado correctamente', 'Cerrar', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
            });

            this.getListFicherosEmpleadosDTO();
          } else if (resultado && resultado.error) {
            this.procesarError(resultado);
          }
        },
        error: (error) => {
          this.procesarError(error);
        },
      });
  }

  /**
   * Guarda un fichero de empleado general.
   * @param employeeId - El ID del empleado.
   * @param file - El fichero a guardar.
   */
  guardarFicheroEmpleadoGeneral(
    employeeId: number,
    file: File
  ): Observable<Files> {
    const url = `${environment.apiUrl}${EMPLEADOS_ENDPOINT}newfilegeneral/${employeeId}`;

    this._uploadingFile$.next(true);

    const formData = new FormData();
    formData.append('file', file);

    return this.httpClient.post<ApiResponse<Files>>(url, formData).pipe(
      map((resultado) => {
        if (resultado && resultado.data) {
          this.snackBar.open('Fichero guardado correctamente', 'Cerrar', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          });

          return resultado.data;
        }

        this.procesarError(resultado);
        throw resultado;
      }),
      catchError((error) => {
        this.procesarError(error);
        return throwError(error);
      }),
      finalize(() => {
        this._uploadingFile$.next(false);
      })
    );
  }

  /**
   * Valida un fichero de empleado.
   * @param idEmployeeFile - El ID del fichero de empleado a validar.
   */
  validarFicheroEmpleado(idEmployeeFile: number): Observable<boolean> {
    this.loading$.next(true);

    const url = `${environment.apiUrl}${EMPLEADOS_ENDPOINT}file/validate/${idEmployeeFile}`;

    return this.httpClient.get<ApiResponse<boolean>>(url).pipe(
      map((resultado) => {
        if (resultado && resultado.data) {
          this.snackBar.open('Fichero validado correctamente', 'Cerrar', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          });

          return true;
        }

        if (resultado && resultado.error) {
          this.procesarError(resultado);
        }

        return false;
      }),
      catchError((error) => {
        this.procesarError(error);
        return of(false);
      }),
      finalize(() => {
        this.loading$.next(false);
      })
    );
  }

  /**
   * Abre el documento asociado al fichero de empleado.
   * @param fichero - El fichero de empleado a visualizar.
   */
  viewDocument(fichero: EmployeesFilesDTO): void {
    const url = `${environment.apiUrl}${FILES_ENDPOINT}getimagefromfiles/${fichero.fichero.uid}/${fichero.fichero.type}`;

    this._downloadingFile$.next(true);

    this.httpClient
      .get(url, { responseType: 'blob' })
      .pipe(
        finalize(() => {
          this._downloadingFile$.next(false);
        })
      )
      .subscribe({
        next: (resultado: Blob) => {
          if (resultado) {
            const objectUrl = window.URL.createObjectURL(resultado);

            const link = document.createElement('a');
            link.href = objectUrl;
            link.target = '_blank';

            document.body.appendChild(link);
            link.click();

            URL.revokeObjectURL(objectUrl);
            document.body.removeChild(link);
          }
        },
        error: (error) => {
          this.procesarErrorDescarga(error);
        },
      });
  }

  // #endregion

  // #region Métodos privados

  private inicializarImage(
    employeesId: number,
    imageBase: string,
    tipo: string,
    size: number,
    tipoGasto: string,
    nombreArchivo: string
  ): EmployeesFileUploadDTO {
    return {
      employeesId: employeesId,
      tipoGasto: tipoGasto,
      nombreArchivo: nombreArchivo,
      base64: imageBase,
      type: tipo,
      size: size,
    };
  }

  // #endregion
}